<template>
    <v-container class="form-container">
        <page-header :title="pageTitle" :subtitle="isCreate || !offer.partner ? null : offer.partner.title" :back="listPath">
            <v-btn v-if="isShow" color="primary" depressed @click="goEdit">
                <v-icon left>mdi-pencil-outline</v-icon>Edit
            </v-btn>
        </page-header>

        <v-form ref="form" v-model="valid" :readonly="readOnly" @submit.prevent="onSubmit">
            <v-card outlined class="mb-4" :loading="loadingEntity">
                <v-card-text>
                    <v-row dense>
                        <v-col cols="12">
                            <entity-search
                                v-model="offer.partner"
                                :search-fn="searchPartners"
                                item-text="title"
                                :rules="required"
                                :readonly="readOnly"
                                label="Partner *"
                                prepend-icon="mdi-storefront-outline"
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-textarea v-model.trim="offer.description" label="Description" rows="3" auto-grow outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-combobox v-model="offer.type" :items="offerTypes" label="Type" :clearable="!readOnly" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="offer.typeEn" label="Type in English" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <date-field v-model="offer.expirationDate" label="Expiration date" :readonly="readOnly" />
                        </v-col>
                        <v-col cols="12" md="6">
                            <div class="d-flex flex-wrap checkbox-row">
                                <v-checkbox v-model="offer.active" label="Active" hide-details />
                                <v-checkbox v-model="offer.secret" label="Secret" hide-details />
                            </div>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <div v-if="!readOnly" class="form-actions">
                <v-btn text @click="goBack">Cancel</v-btn>
                <v-btn color="primary" depressed large type="submit" :loading="saving" :disabled="!valid || loadingEntity">
                    <v-icon left>mdi-content-save-outline</v-icon>{{ isCreate ? 'Create offer' : 'Save changes' }}
                </v-btn>
            </div>
        </v-form>
    </v-container>
</template>

<script>
    import offerService from '@/services/offer-service'
    import partnerService from '@/services/partner-service'
    import formPage from '@/mixins/form-page'
    import PageHeader from '@/components/PageHeader'
    import DateField from '@/components/DateField'
    import EntitySearch from '@/components/EntitySearch'
    import { entityId, pick, toInputDate } from '@/utils/format'

    const OFFER_FIELDS = ['partner', 'description', 'active', 'expirationDate', 'type', 'typeEn', 'secret']

    export default {
        name: 'OfferForm',
        components: { PageHeader, DateField, EntitySearch },
        mixins: [formPage],
        data: () => {
            return {
                entityLabel: 'Offer',
                offerId: null,
                offer: {
                    description: '',
                    active: true,
                    expirationDate: null,
                    partner: null,
                    secret: false,
                    type: '',
                    typeEn: ''
                },
                offerTypes: []
            }
        },
        created() {
            offerService.getOfferTypes().then(resp => {
                this.offerTypes = (resp.data || []).map(row => row.type).filter(Boolean)
            })
            if (this.isCreate) {
                return
            }
            this.offerId = parseInt(this.$route.params.offerId)
            this.loadingEntity = true
            offerService.getOfferById(this.offerId).then(resp => {
                const offer = resp.data.offer
                this.offer = { ...this.offer, ...offer, expirationDate: toInputDate(offer.expirationDate) }
            }).catch(error => {
                this.$notify.error(error, 'Could not load the offer')
            }).finally(() => {
                this.loadingEntity = false
            })
        },
        methods: {
            searchPartners: query => partnerService.searchPartners(query),
            onSubmit() {
                if (!this.validate()) {
                    return
                }
                this.saving = true
                const payload = pick(this.offer, OFFER_FIELDS)
                payload.partner = entityId(this.offer.partner)
                const request = this.isEdit
                    ? offerService.editOfferById(this.offerId, payload)
                    : offerService.createOffer(payload)
                request.then(() => {
                    this.$notify.success(this.isCreate ? 'Offer created' : 'Offer saved')
                    this.goBack()
                }).catch(error => {
                    this.$notify.error(error, 'Could not save the offer')
                }).finally(() => {
                    this.saving = false
                })
            }
        }
    }
</script>
